import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, Pagination, Select as AntSelect } from "antd";
import { Download } from "lucide-react";
import { Button } from "../../components/ui/button";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { PtsChangeRequestService } from "@/services/APIs";
import type { ChangeRequestStatus } from "@/services/APIs/PtsChangeRequestService";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import ChangeRequestFilters from "./ChangeRequestFilters";
import {
  EMPTY_CHANGE_REQUEST_FILTERS,
  type ChangeRequestFilterValues,
} from "./ChangeRequestFilters.constants";
import { ChangeRequestStatusBadge } from "./StatusBadge";
import {
  filterChangeRequests,
  formatDateYmd,
  formatHours,
  formatInr,
  normalizeStatus,
  resolveName,
  statusBadgeClass,
  statusLabel,
} from "./utils";
import { useDragRowSelection } from "../../hooks/useDragRowSelection";

const MANAGEMENT_STATUSES: ChangeRequestStatus[] = [
  "Pending",
  "On Hold",
  "Reject",
  "Approved",
];

const BULK_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "On Hold", label: "On Hold" },
  { value: "Reject", label: "Reject" },
  { value: "Approved", label: "Approved" },
];

type ManagementTabProps = {
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
};

const ManagementTab = ({
  projectNameById,
  employeeNameById,
}: ManagementTabProps) => {
  const queryClient = useQueryClient();
  const [filterValues, setFilterValues] = useState<ChangeRequestFilterValues>(
    EMPTY_CHANGE_REQUEST_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Query for the paginated table list
  const { data, isLoading } = useQuery({
    queryKey: [
      "change-requests",
      "management-list",
      filterValues.projectId,
      filterValues.status,
      filterValues.raisedToClientStatus,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsChangeRequestService.getChangeRequests({
        page: currentPage,
        page_size: pageSize,
        project: filterValues.projectId,
        status: filterValues.status,
        raised_to_client_status: filterValues.raisedToClientStatus,
      }),
  });

  const recentRequests = data?.results;
  const totalCount = data?.count ?? 0;

  const filteredRequests = useMemo(
    () =>
      filterChangeRequests(recentRequests ?? [], {
        projectId: filterValues.projectId,
        status: filterValues.status,
        raisedToClientStatus: filterValues.raisedToClientStatus,
      }),
    [recentRequests, filterValues]
  );

  // Query for stats calculations (non-paginated)
  const { data: statsQueryData } = useQuery({
    queryKey: [
      "change-requests",
      "management-stats",
      filterValues.projectId,
      filterValues.status,
      filterValues.raisedToClientStatus,
    ],
    queryFn: () =>
      PtsChangeRequestService.getChangeRequests({
        page_size: 10000,
        project: filterValues.projectId,
        status: filterValues.status,
        raised_to_client_status: filterValues.raisedToClientStatus,
      }),
  });

  const allRequestsForStats = statsQueryData?.results;

  const stats = useMemo(() => {
    let pending = 0;
    let onHold = 0;
    let approved = 0;
    let rejected = 0;

    (allRequestsForStats ?? []).forEach(item => {
      const status = normalizeStatus(item.status);

      if (status === "pending") {
        pending += 1;
      } else if (status === "on_hold") {
        onHold += 1;
      } else if (status === "approved") {
        approved += 1;
      } else if (status === "reject") {
        rejected += 1;
      }
    });

    return {
      pending,
      onHold,
      approved,
      rejected,
    };
  }, [allRequestsForStats]);

  const {
    selectedIds,
    setSelectedIds,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleMouseDown,
    handleMouseEnter,
  } = useDragRowSelection(filteredRequests);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [filterValues, setSelectedIds]);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, pageSize, setSelectedIds]);

  const bulkStatusMutation = useMutation({
    mutationFn: ({
      ids,
      status,
    }: {
      ids: string[];
      status: ChangeRequestStatus;
    }) => PtsChangeRequestService.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      notify.success("Change requests status updated successfully");
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["change-requests"] });
    },
    onError: (error: any) => {
      notify.error(
        error?.message ?? "Failed to update status for selected items"
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ChangeRequestStatus }) =>
      PtsChangeRequestService.updateChangeRequest(id, { status }),
    onSuccess: result => {
      if (!result?.id) {
        return;
      }
      notify.success("Change request status updated");
      queryClient.invalidateQueries({ queryKey: ["change-requests"] });
    },
  });

  const handleExportCSV = async () => {
    try {
      const res = await PtsChangeRequestService.getChangeRequests({
        page_size: 100000,
        project: filterValues.projectId,
        status: filterValues.status,
        raised_to_client_status: filterValues.raisedToClientStatus,
      });

      const items = res.results;

      const escapeCsv = (val: any) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "CR Number",
        "Project Name",
        "Title",
        "Priority",
        "Requested Date",
        "Expected Completion Date",
        "Assign Employees",
        "Estimated Hours",
        "Description",
        "Status",
        "BA Name",
        "Cost",
        "Billing Configured",
        "Invoice Raised Status",
        "Raised to Client Status",
        "Raised Date",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const projName =
          item.project_name ?? resolveName(item.project, projectNameById);
        const baName = item.ba_name ?? resolveName(item.ba, employeeNameById);

        const rawEmployees = item.assign_employees;
        const employeesList = Array.isArray(rawEmployees)
          ? rawEmployees
              .map((id: string) => employeeNameById.get(id) ?? id)
              .join(", ")
          : typeof rawEmployees === "string"
            ? (rawEmployees as string)
                .split(",")
                .map(
                  (id: string) => employeeNameById.get(id.trim()) ?? id.trim()
                )
                .join(", ")
            : "";

        return [
          escapeCsv(item.cr_number),
          escapeCsv(projName),
          escapeCsv(item.title),
          escapeCsv(item.priority),
          escapeCsv(formatDateYmd(item.requested_date)),
          escapeCsv(formatDateYmd(item.expected_completion_date)),
          escapeCsv(employeesList),
          escapeCsv(item.estimated_hours),
          escapeCsv(item.description),
          escapeCsv(item.status),
          escapeCsv(baName),
          escapeCsv(item.cost),
          escapeCsv(item.billing_configured),
          escapeCsv(item.invoice_raised_status),
          escapeCsv(item.raised_to_client_status),
          escapeCsv(formatDateYmd(item.raised_date)),
          escapeCsv(formatDateYmd(item.created_on)),
          escapeCsv(formatDateYmd(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `change-requests-management-${formatDateYmd(new Date().toISOString())}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Management dashboard
        </h1>
        <Button
          type="button"
          variant="default"
          onClick={handleExportCSV}
          className="h-10 px-5"
        >
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <ChangeRequestFilters
        values={filterValues}
        onChange={setFilterValues}
        idPrefix="management-change-request"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="mt-2 text-3xl font-bold text-amber-500">
              {stats.pending}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">On Hold</p>
            <p className="mt-2 text-3xl font-bold text-sky-600">
              {stats.onHold}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="mt-2 text-3xl font-bold text-emerald-600">
              {stats.approved}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Reject</p>
            <p className="mt-2 text-3xl font-bold text-rose-600">
              {stats.rejected}
            </p>
          </CardContent>
        </Card>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 rounded-lg border border-[#BAE0FF] bg-[#F0F7FF] px-4 py-2.5">
          <span className="text-sm font-medium text-gray-800">
            {selectedIds.length} selected
          </span>
          <div className="w-44">
            <AntSelect
              value={undefined}
              placeholder="Change Status"
              disabled={bulkStatusMutation.isPending}
              loading={bulkStatusMutation.isPending}
              onChange={value => {
                if (value) {
                  bulkStatusMutation.mutate({
                    ids: selectedIds,
                    status: value,
                  });
                }
              }}
              className="w-full"
              options={BULK_STATUS_OPTIONS}
            />
          </div>
          <button
            type="button"
            onClick={() => setSelectedIds([])}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="w-[48px] px-4 py-3 text-center">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      disabled={isLoading || filteredRequests.length === 0}
                    />
                  </th>
                  <th className="px-5 py-3 font-medium">CR Number</th>
                  <th className="px-5 py-3 font-medium">Project</th>
                  <th className="px-5 py-3 font-medium">Title</th>
                  <th className="px-5 py-3 font-medium">Estimated Hours</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Requested Date</th>
                  <th className="px-5 py-3 font-medium">
                    Raised to Client Status
                  </th>
                  <th className="px-5 py-3 font-medium">Raised Date</th>
                  <th className="px-5 py-3 font-medium">BA</th>
                  <th className="px-5 py-3 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading change requests...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No change requests found.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td
                        className="w-[48px] cursor-pointer select-none px-4 py-3.5 text-center"
                        onMouseDown={e => handleMouseDown(item.id, index, e)}
                        onMouseEnter={() => handleMouseEnter(item.id, index)}
                      >
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          className="pointer-events-none"
                        />
                      </td>
                      <td className="px-5 py-3.5 font-medium text-[#111827]">
                        {item.cr_number || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.project_name ??
                          resolveName(item.project, projectNameById)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.title || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatHours(item.estimated_hours)}
                      </td>
                      <td className="px-5 py-3.5">
                        <Select
                          value={String(item.status || "Pending")}
                          disabled={
                            normalizeStatus(item.status) === "reject" ||
                            (statusMutation.isPending &&
                              statusMutation.variables?.id === item.id)
                          }
                          onValueChange={status =>
                            statusMutation.mutate({
                              id: item.id,
                              status,
                            })
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              "h-8 w-[155px] rounded-full px-3 text-xs font-medium shadow-none focus:ring-1 focus:ring-offset-0",
                              statusBadgeClass(item.status),
                              normalizeStatus(item.status) === "reject" &&
                                "cursor-not-allowed opacity-80"
                            )}
                          >
                            <SelectValue>
                              {statusLabel(item.status)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {MANAGEMENT_STATUSES.map(status => (
                              <SelectItem key={status} value={status}>
                                {statusLabel(status)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateYmd(item.requested_date)}
                      </td>
                      <td className="px-5 py-3.5">
                        {item.raised_to_client_status ? (
                          <ChangeRequestStatusBadge
                            status={item.raised_to_client_status}
                          />
                        ) : (
                          <span className="text-[#374151]">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateYmd(item.raised_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.ba_name ?? resolveName(item.ba, employeeNameById)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatInr(item.cost)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  if (size && size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                    return;
                  }
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={["50", "100", "200", "500"]}
                defaultPageSize={100}
                disabled={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagementTab;
